import {
  DashboardFilter,
  DateDashboardFilter,
  NumberDashboardFilter,
  UnlinkedDashboardFilter,
} from '../types';
import {
  APIDashboardFilterExpression,
  APIDashboardFilterParameter,
  APIFieldType,
  APIInitialDashboardFilterParameter,
  Operator,
} from '@ardoq/api-types';

const isUnlinkedDashboardFilter = (
  dashboardFilter: DashboardFilter | UnlinkedDashboardFilter
): dashboardFilter is UnlinkedDashboardFilter =>
  dashboardFilter.linkCounts.total === 0;

const isLinkedDashboardFilter = (
  dashboardFilter: DashboardFilter | UnlinkedDashboardFilter
): dashboardFilter is DashboardFilter => dashboardFilter.linkCounts.total > 0;

const isNumberDashboardFilter = (
  dashboardFilter: DashboardFilter | UnlinkedDashboardFilter
): dashboardFilter is NumberDashboardFilter =>
  dashboardFilter.type === APIFieldType.NUMBER;

const isDateDashboardFilter = (
  dashboardFilter: DashboardFilter | UnlinkedDashboardFilter
): dashboardFilter is DateDashboardFilter =>
  dashboardFilter.type === APIFieldType.DATE_ONLY ||
  dashboardFilter.type === APIFieldType.DATE_TIME;

const hasValue = (
  dashboardFilter: DashboardFilter | UnlinkedDashboardFilter
): boolean => {
  if (isUnlinkedDashboardFilter(dashboardFilter)) {
    return false;
  } else if (
    isNumberDashboardFilter(dashboardFilter) ||
    isDateDashboardFilter(dashboardFilter)
  ) {
    return (
      dashboardFilter.value !== null ||
      dashboardFilter.operator === Operator.IS_EMPTY ||
      dashboardFilter.operator === Operator.IS_NOT_EMPTY
    );
  }
  return dashboardFilter.values.length > 0;
};

const toFilterExpressions = (
  dashboardFilter: DashboardFilter | UnlinkedDashboardFilter
): APIDashboardFilterExpression[] => {
  if (isUnlinkedDashboardFilter(dashboardFilter)) {
    return [];
  } else if (
    isNumberDashboardFilter(dashboardFilter) ||
    isDateDashboardFilter(dashboardFilter)
  ) {
    return hasValue(dashboardFilter)
      ? [{ value: dashboardFilter.value, operator: dashboardFilter.operator }]
      : [];
  }
  return dashboardFilter.values;
};

const toFilterExpressionsById = (
  dashboardFilters: Array<DashboardFilter | UnlinkedDashboardFilter>
): Record<string, APIDashboardFilterExpression[]> =>
  dashboardFilters.reduce<Record<string, APIDashboardFilterExpression[]>>(
    (byId, filter) => {
      const filterExpressions = toFilterExpressions(filter);
      if (filterExpressions.length !== 0) {
        byId[filter.id] = filterExpressions;
      }
      return byId;
    },
    {}
  );

const toParameter = (
  dashboardFilter: DashboardFilter | UnlinkedDashboardFilter
): APIDashboardFilterParameter | APIInitialDashboardFilterParameter => {
  if (isUnlinkedDashboardFilter(dashboardFilter)) {
    return {
      id: dashboardFilter.id,
      name: dashboardFilter.name,
      type: undefined,
      fieldApiName: undefined,
    };
  }
  return {
    id: dashboardFilter.id,
    name: dashboardFilter.name,
    type: dashboardFilter.type,
    fieldApiName: dashboardFilter.field.name,
  };
};

export const dashboardFilterOperations = {
  isUnlinkedDashboardFilter,
  hasValue,
  toFilterExpressionsById,
  toParameter,
  isLinkedDashboardFilter,
};
